import { useLingui } from "@lingui/react/macro";
import type { WorkspaceAppId, WorkspaceBounds } from "@rakazo/contracts";
import { WORKSPACE_APPS, workspaceDestination } from "@rakazo/contracts";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Input,
  OverlayScope,
  toast,
} from "@rakazo/ui-web";
import { Copy, Grid2X2, Home, PanelLeft, PanelRight, X } from "lucide-react";
import type { ReactNode, RefObject } from "react";
import {
  Activity,
  createContext,
  Suspense,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { Location } from "react-router-dom";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { desktopBridge } from "../lib/desktop";
import { selectedSpaceId } from "../lib/rpc";
import {
  findWorkspaceWindow,
  locationFor,
  useWorkspaceWindows,
  windowHref,
} from "../lib/use-workspace-windows";
import {
  WorkspaceScopeContext,
  WorkspaceSearchHotkeyContext,
  WorkspaceWindowFocusContext,
  WorkspaceWindowIdContext,
  WorkspaceWindowNavigateContext,
  WorkspaceWindowTitleContext,
} from "../lib/workspace-local-state";
import { isCommandPaletteHotkey } from "../pages/shell/command-palette-hotkey";
import { WindowChrome } from "../pages/WindowChrome";
import { CodeField } from "./CodeField";
import { ErrorBoundary } from "./ErrorBoundary";
import { RouteLoadingState } from "./RouteLoadingState";
import { WorkspaceAppIcon } from "./WorkspaceAppIcon";
import { WorkspaceDock } from "./WorkspaceDock";
import { WorkspaceSearch } from "./WorkspaceSearch";
import { WorkspaceWindowFrame } from "./WorkspaceWindowFrame";

const SwitcherContext = createContext<(() => void) | null>(null);
const DesktopContext = createContext(false);
export function useWorkspaceDesktop() {
  return useContext(DesktopContext);
}
const href = (location: Location) => `${location.pathname}${location.search}`;

export function WorkspaceSwitcherButton() {
  const show = useContext(SwitcherContext);
  const desktop = useWorkspaceDesktop();
  const { t } = useLingui();
  if (!show || desktop) return null;
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      className="app-no-drag shrink-0 text-muted-foreground"
      aria-label={t`Switch apps`}
      title={t`Switch apps (Alt + J)`}
      onClick={show}
    >
      <Grid2X2 size={17} />
    </Button>
  );
}

/** The desktop area windows live in, measured in its own coordinate space. */
function useDesktopRect(ref: RefObject<HTMLElement | null>): WorkspaceBounds {
  const [rect, setRect] = useState<WorkspaceBounds>({ x: 0, y: 0, width: 0, height: 0 });
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    let pending = 0;
    const measure = () =>
      setRect((previous) => {
        const width = node.clientWidth;
        const height = node.clientHeight;
        return previous.width === width && previous.height === height
          ? previous
          : { x: 0, y: 0, width, height };
      });
    measure();
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(pending);
      pending = requestAnimationFrame(measure);
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(pending);
    };
  }, [ref]);
  return rect;
}

/** A bounded set of existing app screens in real windows, with hidden windows' effects stopped. */
export function WorkspaceSession({ render }: { render: (location: Location) => ReactNode }) {
  const scope = useContext(WorkspaceScopeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { t } = useLingui();
  const destination = workspaceDestination(href(location));

  const surface = useRef<HTMLDivElement>(null);
  const desktopRect = useDesktopRect(surface);
  const session = useWorkspaceWindows(scope, desktopRect);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    // Markdown and generated results use ordinary anchors. Keep owned resources in
    // the current workspace instead of a new browser tab or native OAuth popup.
    const openLink = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const anchor = event.target instanceof Element ? event.target.closest("a[href]") : null;
      if (
        !(anchor instanceof HTMLAnchorElement) ||
        anchor.hasAttribute("download") ||
        !["", "_self", "_blank"].includes(anchor.target) ||
        anchor.getAttribute("href")?.startsWith("#")
      )
        return;
      let url: URL;
      try {
        url = new URL(anchor.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin || url.username || url.password) return;
      const path = url.pathname + url.search + url.hash;
      if (!workspaceDestination(path)) return;
      event.preventDefault();
      navigate(path);
    };
    document.addEventListener("click", openLink);
    return () => document.removeEventListener("click", openLink);
  }, [navigate]);

  // Home is the desktop itself, so it never becomes a window. Waiting for the measured desktop
  // keeps the first window from being placed against a zero-sized one.
  const { show, ready, showDesktop, takeRefusal } = session;
  const targetId = destination?.id ?? null;
  const targetName = destination?.name;
  const routeOpened = useRef(false);
  // A deep link wins only until the window it addressed has taken it. After that the window owns
  // its own location, so moving one window does not get overruled by the shared address.
  const syncedRoute = useRef<string | null>(null);
  const deepLinkPending = syncedRoute.current !== location.key;
  const routeWindow =
    destination &&
    findWorkspaceWindow(
      session.windows,
      destination.id,
      location,
      session.focus,
      location.state?.workspaceOpen === "resource" ? "resource" : "reuse",
      typeof location.state?.workspaceWindowId === "string"
        ? location.state.workspaceWindowId
        : undefined,
    );
  // Held in a ref so translations cannot change how often the route effect runs.
  const announceRefusal = useRef((name: string) => {
    toast(t`${name} needs a free window. Close a window to open it.`);
  });
  announceRefusal.current = (name: string) => {
    toast(t`${name} needs a free window. Close a window to open it.`);
  };
  useEffect(() => {
    if (!ready || !targetId || !targetName) return;
    // An incoming link must restore its target even when navigation replaced the history entry.
    // Only later replacements can be a retained window tidying its own address.
    const foreground = !routeOpened.current || navigationType !== "REPLACE";
    routeOpened.current = true;
    // Going Home means showing the desktop: the work is put away, never closed.
    if (targetId === "home") showDesktop();
    // A replaced address is a window tidying its own query, not someone opening it, so it must
    // not take the foreground away from whatever the person is actually working in.
    else
      show(
        { id: targetId, name: targetName },
        location,
        foreground,
        location.state?.workspaceOpen === "resource" ? "resource" : "reuse",
        typeof location.state?.workspaceWindowId === "string"
          ? location.state.workspaceWindowId
          : undefined,
      );
    syncedRoute.current = location.key;
    const turnedAway = takeRefusal();
    if (turnedAway) announceRefusal.current(turnedAway);
  }, [show, showDesktop, takeRefusal, ready, targetId, targetName, location, navigationType]);

  const focused = session.windows.find((item) => item.id === session.focus) ?? null;
  const focusedRef = useRef(focused);
  focusedRef.current = focused;

  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (isCommandPaletteHotkey(event)) {
        event.preventDefault();
        setSearchOpen((value) => !value);
        return;
      }
      if (!event.altKey || event.metaKey || event.ctrlKey) return;
      const current = focusedRef.current;
      const act = (run: () => void) => {
        event.preventDefault();
        run();
      };
      if (event.code === "KeyJ") return act(() => setOpen((value) => !value));
      if (event.code === "KeyD") return act(session.toggleDesktop);
      if (!current) return;
      if (event.code === "KeyH") return act(() => session.setState(current.id, "minimized"));
      if (event.code === "Enter")
        return act(() =>
          session.setState(current.id, current.state === "maximized" ? "normal" : "maximized"),
        );
      if (event.code === "BracketLeft") return act(() => session.snap(current.id, "left"));
      if (event.code === "BracketRight") return act(() => session.snap(current.id, "right"));
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [session.toggleDesktop, session.setState, session.snap]);

  /**
   * Another window of the same app, for working on two documents or two conversations at once.
   * It takes the foreground directly rather than navigating, because a navigation would be
   * matched to the app's existing window and reuse that instead of the new one.
   */
  function openInNewWindow(path: string) {
    // A blank editor is a new document, not another view of the legacy "local" draft.
    // Explicit file/draft targets still keep their resource identity.
    const target = path === "/app/editor" ? `${path}?draft=${crypto.randomUUID()}` : path;
    const destination = workspaceDestination(target);
    if (!destination || destination.id === "home") return;
    session.show(destination, locationFor(destination.path, `new:${Date.now()}`), true, "new");
    const turnedAway = session.takeRefusal();
    if (turnedAway) announceRefusal.current(turnedAway);
  }

  function launch(path: string) {
    setOpen(false);
    setQuery("");
    navigate(path);
  }
  function close(id: string) {
    session.close(id);
  }

  const options = [
    { id: "home", name: t`Home`, web: "/app" },
    ...WORKSPACE_APPS,
    { id: "apps", name: t`Apps`, web: "/app/workspace/apps" },
    { id: "notifications", name: t`Notifications`, web: "/app/notifications" },
    { id: "shared-bots", name: t`Shared bots`, web: "/app/shared-bots" },
  ];
  const matches = options.filter((app) => app.name.toLowerCase().includes(query.toLowerCase()));
  const chrome = Boolean(desktopBridge());
  // Raising a window must only change its depth. Reordering the DOM would move the node mid-drag,
  // and moving a node releases its pointer capture, cancelling the gesture that raised it.
  const stacking = new Map(session.windows.map((item, index) => [item.id, index]));
  const painted = [...session.windows].sort((a, b) => a.id.localeCompare(b.id));

  return (
    <SwitcherContext.Provider value={() => setOpen(true)}>
      <DesktopContext.Provider value>
        <WorkspaceSearchHotkeyContext.Provider value={false}>
          <div className="workspace-frame relative isolate flex h-full min-h-0 min-w-0 flex-col">
            {/* Avoid continuously repainting the blurred backdrop behind a desktop dialog. */}
            <CodeField swimming={!open && !searchOpen} />
            {/* Native window controls belong to the desktop, so no app window can collide. */}
            {chrome ? (
              <div
                data-testid="workspace-window-controls"
                className="app-drag relative z-10 flex h-8 shrink-0 items-center px-3"
              >
                <WindowChrome />
              </div>
            ) : null}
            {/* Windows live inside the desktop and are clipped by it: a window briefly larger than a
              newly shrunk viewport must not scroll the whole page. */}
            <div
              ref={surface}
              className="workspace-session relative z-10 min-h-0 min-w-0 flex-1 overflow-hidden"
              data-testid="workspace-session"
              data-windows={session.visible.length || undefined}
            >
              {/* The desktop sits under the work and keeps it when a window is shown or hidden.
                While windows are up it is a backdrop: it must not offer a second copy of their
                controls to the pointer or to a screen reader. Show Desktop hands it back. */}
              <div
                className="absolute inset-0 z-0 overflow-auto"
                data-workspace-desktop
                aria-hidden={session.visible.length > 0 || undefined}
                inert={session.visible.length > 0 || undefined}
              >
                <WorkspaceWindowFocusContext.Provider value={session.visible.length === 0}>
                  <ErrorBoundary fallback={<WorkspaceAppFailed name={t`Home`} />}>
                    <Suspense fallback={<RouteLoadingState />}>
                      {render({
                        pathname: "/app",
                        search: "",
                        hash: "",
                        state: null,
                        key: "desktop",
                      })}
                    </Suspense>
                  </ErrorBoundary>
                </WorkspaceWindowFocusContext.Provider>
              </div>
              {painted.map((item) => {
                const visible = item.state !== "minimized";
                const index = stacking.get(item.id) ?? 0;
                return (
                  <Activity key={item.id} mode={visible ? "visible" : "hidden"}>
                    <WorkspaceWindowFrame
                      window={item}
                      overlayParent={surface.current}
                      index={index}
                      focused={session.focus === item.id}
                      onFocus={() => session.raise(item.id)}
                      onMove={(bounds) => session.setBounds(item.id, bounds)}
                      onState={(state) => session.setState(item.id, state)}
                      onClose={() => close(item.id)}
                      onNewWindow={
                        item.app === "notifications"
                          ? undefined
                          : () =>
                              openInNewWindow(
                                WORKSPACE_APPS.find((app) => app.id === item.app)?.web ??
                                  windowHref(item.location),
                              )
                      }
                    >
                      <WorkspaceWindowIdContext.Provider value={item.id}>
                        <WorkspaceWindowTitleContext.Provider value={session.setTitle}>
                          <WorkspaceWindowNavigateContext.Provider
                            value={(path) => session.navigateWindow(item.id, path)}
                          >
                            <WorkspaceWindowFocusContext.Provider value={session.focus === item.id}>
                              <ErrorBoundary fallback={<WorkspaceAppFailed name={item.name} />}>
                                <Suspense fallback={<RouteLoadingState />}>
                                  {/* A current deep link must win on the first render, before a restored
                                  app can read or redirect its previous conversation. */}
                                  {render(
                                    item.id === routeWindow?.id && deepLinkPending
                                      ? location
                                      : item.location,
                                  )}
                                </Suspense>
                              </ErrorBoundary>
                            </WorkspaceWindowFocusContext.Provider>
                          </WorkspaceWindowNavigateContext.Provider>
                        </WorkspaceWindowTitleContext.Provider>
                      </WorkspaceWindowIdContext.Provider>
                    </WorkspaceWindowFrame>
                  </Activity>
                );
              })}
            </div>
            <WorkspaceDock
              windows={session.windows.map((item) => ({
                id: item.id,
                app: item.app,
                name: item.title ?? item.name,
                path: windowHref(item.location),
                minimized: item.state === "minimized",
              }))}
              active={session.desktopShown ? undefined : focused?.id}
              onOpen={launch}
              onActivate={session.activate}
              onNewWindow={openInNewWindow}
              onHome={session.toggleDesktop}
              onLauncher={() => setOpen(true)}
            />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-md gap-3 p-5">
              <DialogTitle>{t`Apps`}</DialogTitle>
              <Input
                aria-label={t`Find an app`}
                placeholder={t`Find an app…`}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" || !matches[0]) return;
                  event.preventDefault();
                  const option = matches[0];
                  const running = session.windows.find((item) => item.app === option.id);
                  launch(running ? windowHref(running.location) : option.web);
                }}
              />
              <fieldset
                className="max-h-[60vh] min-w-0 overflow-y-auto pe-2"
                aria-label={t`Workspace apps`}
              >
                {matches.map((option) => {
                  const running = session.windows.find((item) => item.app === option.id);
                  return (
                    <div
                      key={option.id}
                      className="group flex min-w-0 items-center gap-1 rounded-lg hover:bg-muted/50"
                    >
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center gap-3 px-2 py-2 text-left text-sm"
                        onClick={() => launch(running ? windowHref(running.location) : option.web)}
                      >
                        {option.id === "home" ? (
                          <span className="grid size-6 shrink-0 place-items-center">
                            <Home size={16} />
                          </span>
                        ) : (
                          <WorkspaceAppIcon id={option.id as WorkspaceAppId} compact />
                        )}
                        <span className="flex-1 truncate">{option.name}</span>
                        {running && (
                          <span
                            role="img"
                            className="h-1 w-1 rounded-full bg-foreground/60"
                            aria-label={t`Open`}
                          />
                        )}
                      </button>
                      {running && option.id !== "home" && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t`New ${option.name} window`}
                          onClick={() => {
                            openInNewWindow(option.web);
                            setOpen(false);
                          }}
                        >
                          <Copy size={15} />
                        </Button>
                      )}
                      {running && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t`Snap ${option.name} left`}
                            onClick={() => {
                              session.snap(running.id, "left");
                              setOpen(false);
                            }}
                          >
                            <PanelLeft size={15} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t`Snap ${option.name} right`}
                            onClick={() => {
                              session.snap(running.id, "right");
                              setOpen(false);
                            }}
                          >
                            <PanelRight size={15} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t`Close ${option.name}`}
                            onClick={() => close(running.id)}
                          >
                            <X size={15} />
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })}
                {!matches.length && (
                  <p className="p-3 text-sm text-muted-foreground">{t`No apps found`}</p>
                )}
              </fieldset>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  session.toggleDesktop();
                  setOpen(false);
                }}
              >{t`Show desktop`}</Button>
            </DialogContent>
          </Dialog>
          <WorkspaceSearch
            spaceId={selectedSpaceId() ?? ""}
            open={searchOpen}
            onOpenChange={setSearchOpen}
          />
        </WorkspaceSearchHotkeyContext.Provider>
      </DesktopContext.Provider>
    </SwitcherContext.Provider>
  );
}

/** One unavailable app must not unmount the other open documents and conversations. */
function WorkspaceAppFailed({ name }: { name: string }) {
  const { t } = useLingui();
  return (
    <div className="app-canvas flex h-full flex-col bg-background">
      <header className="app-drag flex h-14 shrink-0 items-center gap-4 px-5">
        {desktopBridge() && <WindowChrome />}
        <WorkspaceSwitcherButton />
        <span className="text-xs text-muted-foreground">{name}</span>
      </header>
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6">
        <p role="status" className="text-sm text-muted-foreground">{t`Could not load`}</p>
        <Button variant="secondary" onClick={() => window.location.reload()}>
          {t`Refresh`}
        </Button>
      </div>
    </div>
  );
}
